import api from "../api";


const BASE = "/api/confirmations";

function base64urlToBytes(value) {
  const normalized = String(value || "").replace(/-/g, "+").replace(/_/g, "/");
  const binary = window.atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "="));
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

function bytesToBase64url(value) {
  const bytes = new Uint8Array(value || new ArrayBuffer(0));
  let binary = "";
  bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
  return window.btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

export function getConfirmationDeviceId() {
  const key = "eternua_confirmation_device_id";
  let value = window.localStorage.getItem(key);
  if (!value) {
    value = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    window.localStorage.setItem(key, value);
  }
  return value;
}

export function confirmationDeviceClass() {
  const width = Number(window.innerWidth || 0);
  if (width <= 640) return "mobile";
  if (width <= 1024) return "tablet";
  return "desktop";
}

function withDevice(payload) {
  return { ...payload, device_id: getConfirmationDeviceId(), device_label: navigator.platform || "Navigateur web" };
}

export function createConfirmationId(prefix = "confirmation") {
  const random = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${prefix}-${random}`;
}

export async function createConfirmationChallenge(payload) {
  const response = await api.post(`${BASE}/challenge/`, withDevice(payload));
  return response.data;
}

export async function verifyConfirmationSecurity(payload) {
  const response = await api.post(`${BASE}/verify/`, payload);
  return response.data;
}

export async function createConfirmationReceipt(payload) {
  const response = await api.post(`${BASE}/receipts/`, withDevice(payload));
  return response.data;
}

export async function listConfirmationReceipts(params = {}) {
  const response = await api.get(`${BASE}/receipts/`, { params });
  return Array.isArray(response.data?.results) ? response.data.results : [];
}

export async function recordConfirmationEvent(payload) {
  try {
    const response = await api.post(`${BASE}/events/`, { device_class: confirmationDeviceClass(), ...payload });
    return response.data;
  } catch (_error) {
    return null;
  }
}

export async function evaluateConfirmationRisk(payload) {
  const response = await api.post(`${BASE}/risk/`, withDevice(payload));
  return response.data;
}

export async function getConfirmationPreferences() {
  const response = await api.get(`${BASE}/preferences/`);
  return response.data;
}

export async function getConfirmationRollout(universe) {
  const response = await api.get(`${BASE}/rollout/`, { params: { universe } });
  return response.data;
}

export async function updateConfirmationPreferences(payload) {
  const response = await api.patch(`${BASE}/preferences/`, payload);
  return response.data;
}

export async function getLegalConsentCenter() {
  const response = await api.get(`${BASE}/consents/`);
  return response.data;
}

export async function recordLegalConsent(payload) {
  const response = await api.post(`${BASE}/consents/`, withDevice(payload));
  return response.data;
}

export async function downloadLegalConsentExport() {
  const response = await api.get(`${BASE}/consents/export/`, { responseType: "blob" });
  const url = window.URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = url;
  link.download = "eternua-consentements.json";
  link.click();
  window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}

export async function submitConfirmationFeedback(payload) {
  const response = await api.post(`${BASE}/feedback/`, { device_class: confirmationDeviceClass(), ...payload });
  return response.data;
}

export async function cancelConfirmationReceipt(publicId, reason = "undo") {
  const response = await api.post(`${BASE}/receipts/${publicId}/cancel/`, { reason });
  return response.data;
}

export async function downloadConfirmationCertificate(publicId) {
  const response = await api.get(`${BASE}/receipts/${publicId}/certificate.pdf`, { responseType: "blob" });
  const url = window.URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = url;
  link.download = `recu-eternua-${publicId}.pdf`;
  link.click();
  window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}

export async function getPublicConfirmationReceipt(publicId, token) {
  const response = await api.get(`${BASE}/public/${publicId}/${token}/`);
  return response.data;
}

export async function listPasskeys() {
  const response = await api.get(`${BASE}/passkeys/`);
  return response.data;
}

export async function deletePasskey(publicId) {
  await api.delete(`${BASE}/passkeys/${publicId}/`);
}

function normalizeCreationOptions(options) {
  return {
    ...options,
    challenge: base64urlToBytes(options.challenge),
    user: { ...options.user, id: base64urlToBytes(options.user.id) },
    excludeCredentials: (options.excludeCredentials || []).map((item) => ({ ...item, id: base64urlToBytes(item.id) })),
  };
}

function normalizeRequestOptions(options) {
  return {
    ...options,
    challenge: base64urlToBytes(options.challenge),
    allowCredentials: (options.allowCredentials || []).map((item) => ({ ...item, id: base64urlToBytes(item.id) })),
  };
}

function serializeCredential(credential) {
  const response = {
    clientDataJSON: bytesToBase64url(credential.response.clientDataJSON),
  };
  if (credential.response.attestationObject) {
    response.attestationObject = bytesToBase64url(credential.response.attestationObject);
    response.transports = credential.response.getTransports?.() || [];
  } else {
    response.authenticatorData = bytesToBase64url(credential.response.authenticatorData);
    response.signature = bytesToBase64url(credential.response.signature);
    response.userHandle = credential.response.userHandle ? bytesToBase64url(credential.response.userHandle) : null;
  }
  return { id: credential.id, rawId: bytesToBase64url(credential.rawId), type: credential.type, response, clientExtensionResults: credential.getClientExtensionResults?.() || {} };
}

export async function registerPasskey(label = "Passkey personnelle") {
  if (!window.PublicKeyCredential || !navigator.credentials) throw new Error("Les Passkeys ne sont pas disponibles dans ce navigateur.");
  const optionsResponse = await api.post(`${BASE}/passkeys/registration/options/`, {});
  const credential = await navigator.credentials.create({ publicKey: normalizeCreationOptions(optionsResponse.data.public_key) });
  const response = await api.post(`${BASE}/passkeys/registration/verify/`, { ceremony_token: optionsResponse.data.ceremony_token, credential: serializeCredential(credential), label });
  return response.data;
}

export async function verifyWithPasskey(payload) {
  if (!window.PublicKeyCredential || !navigator.credentials) throw new Error("Les Passkeys ne sont pas disponibles dans ce navigateur.");
  const optionsResponse = await api.post(`${BASE}/passkeys/authentication/options/`, payload);
  const credential = await navigator.credentials.get({ publicKey: normalizeRequestOptions(optionsResponse.data.public_key) });
  const response = await api.post(`${BASE}/passkeys/authentication/verify/`, { ceremony_token: optionsResponse.data.ceremony_token, credential: serializeCredential(credential) });
  return response.data;
}
